/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

//react imports
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';

//react native imports
import { useLogger } from '@airasia-rn/core-logger';
import { useTranslation } from 'react-i18next';
import { Text, View, StyleSheet, Platform } from 'react-native';

//phoenix imports
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils/utils';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  useAppConfig,
} from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import ContentCardGroupView from '@airasia-phoenix/react-native/widgets/ContentCardGroupView';
import { Constants } from './constants';
import { useHotelDealsCarouselHandlers } from './useHotelDealsCarouselHandlers';
import {
  getHotelDealsCarouselSignature,
  mapToVerticalContentCardProps,
} from './utils';
import { useHotelRecommendations } from '../../hooks/hotels';
import { useHandleHotelListNavigation } from '../../hooks/useHandleHotelListNavigation';
import { useHotelConfig } from '../../configs/HotelConfigProvider';
import { MOVE_HOTEL_WEB_HOSTS } from '../../configs/constants';
import {
  HotelDealsCarouselItemProps,
  HotelDealsItemAPIResponse,
} from '../../types';
import { SkeletonLoaderWrapper } from '../../components/SkeletonLoaderWrapper';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';

const HotelDealsCarouselView = () => {
  const [carouselData, setCarouselData] = useState<
    HotelDealsCarouselItemProps[]
  >([]);
  const { t } = useTranslation('hotels');
  const { logEvent } = useLogger();
  const hasTrackedRef = useRef(false);
  const { config: appConfigWrapper } = useAppConfig();
  const { currency: activeCurrency } = useCurrency();
  const { config: currentHotelConfig } = useHotelConfig();
  const superAppContext = useSuperAppContext();
  const { navigateToHotelDetailsUrl } = useHandleHotelListNavigation();
  const { EVENT_NAMES } = SearchWidgetMeta;

  const {
    data: recommendationResponse,
    isLoading,
    error,
  } = useHotelRecommendations();

  const items = useMemo(
    () => recommendationResponse?.groups?.[0]?.items ?? [],
    [recommendationResponse],
  );

  const { handleScroll, initCounters } = useHotelDealsCarouselHandlers({
    recommendationItems: items,
    setCarouselData: useCallback(() => {}, []),
  });

  // Memoize tracking items to optimize performance
  const trackingItems = useMemo(() => {
    if (!items.length) return [];

    return items.map((item, index) => {
      const price = Number(item?.details?.price || 0);

      return {
        item_id: item?.details?.productid || '',
        item_name: item?.title ?? '',
        item_category: `${item?.details?.checkindate} - ${item?.details?.checkoutdate}`,
        item_category2: item?.details?.recommendationtype || '',
        item_category3: item?.details?.strikethroughprice ?? '',
        item_category4: item?.details?.totalpax ?? '',
        item_category5: item?.location ?? '',
        quantity: '1',
        price,
        index: index + 1,
      };
    });
  }, [items]);

  const handleHotelDealPress = useCallback(
    (item: HotelDealsItemAPIResponse) => {
      const price = Number(item?.details?.price || 0);

      const recommendationTitle =
        recommendationResponse?.groups[0]?.title ||
        t('searchHotelPage.hotelDeals.title');

      const details = item.details || {};
      const recommendationType = details.recommendationtype || '';
      const destinationCountry = details.destinationcountry || '';

      const analyticsParams = SearchWidgetEvent.hotelDealsClickedEvent({
        recommendationTitle,
        recommendationType,
        hotelId: details.productid || '',
        hotelName: item.title ?? '',
        destination: item.location ?? '',
        destinationCategory: details.destinationcategory || '',
        destinationCountry,
        checkInDate: details.checkindate || '',
        checkOutDate: details.checkoutdate || '',
        lengthOfStay: 3,
        rooms: 1,
        currency: details.currency?.toUpperCase() || '',
        price,
        strikeThroughPrice: details.strikethroughprice || '',
        totalPax: details.totalpax || '',
        adult: 2,
        child: 0,
        infant: 0,
      });
      logEvent(EVENT_NAMES.HOTEL_DEALS_CLICKED, analyticsParams);
      const currentGeoId = superAppContext?.geoId;

      const currency =
        details.currency?.toUpperCase() || activeCurrency || DEFAULT_CURRENCY;
      const countryCode =
        currentHotelConfig.geoId ||
        appConfigWrapper.geoId ||
        currentGeoId ||
        'MY';
      const languageId = appConfigWrapper.locale || DEFAULT_LANGUAGE;

      let rawTargetUrl = item?.deepLink || '';
      if (rawTargetUrl) {
        try {
          const parsedUrl = new URL(rawTargetUrl);
          rawTargetUrl = `${parsedUrl.origin}${parsedUrl.pathname}`;
        } catch (e) {
          rawTargetUrl = rawTargetUrl.split('?')[0];
        }
      }

      let targetUrl = rawTargetUrl;
      const isStaging =
        appConfigWrapper.env === 'stg' ||
        (__DEV__ && typeof __DEV__ !== 'undefined');

      if (!targetUrl) {
        const fallbackContentId =
          (item.metaData as Record<string, any>)?.contentId ||
          details.productid ||
          '';

        targetUrl = isStaging
          ? `${MOVE_HOTEL_WEB_HOSTS.stg}/${fallbackContentId}`
          : `${MOVE_HOTEL_WEB_HOSTS.prd}/${fallbackContentId}`;
      }
      let detailsPageUrl = targetUrl;
      // Production and staging share the same /hotel mount layout.

      if (detailsPageUrl.includes('?')) {
        detailsPageUrl = detailsPageUrl.replace(/(\/[^?/\s]+)(\?)/, '$1/$2');
      } else if (!detailsPageUrl.endsWith('/')) {
        detailsPageUrl = `${detailsPageUrl}/`;
      }

      const contentId =
        (item.metaData as Record<string, any>)?.contentId ||
        details.productid ||
        '';
      const extraParams = `?location_id=${contentId}&country_code=${countryCode}&currency=${currency}&languageId=${languageId}&source=hsw`;

      detailsPageUrl = `${detailsPageUrl}${extraParams}`;
      navigateToHotelDetailsUrl(detailsPageUrl);
    },
    [
      recommendationResponse,
      logEvent,
      EVENT_NAMES.HOTEL_DEALS_CLICKED,
      activeCurrency,
      appConfigWrapper,
      currentHotelConfig,
      superAppContext,
      navigateToHotelDetailsUrl,
    ],
  );

  const initialCards = useMemo(() => {
    if (!items.length) return [];
    return mapToVerticalContentCardProps(
      items.slice(0, Constants.INITIAL_LOAD_IMAGE_COUNT),
      t,
      handleHotelDealPress,
    );
  }, [items, t, handleHotelDealPress]);

  const initialCardsSignature = useMemo(
    () => getHotelDealsCarouselSignature(initialCards),
    [initialCards],
  );

  const carouselDataSignature = useMemo(
    () => getHotelDealsCarouselSignature(carouselData),
    [carouselData],
  );

  useEffect(() => {
    if (!initialCards.length) return;
    if (initialCardsSignature === carouselDataSignature) return;

    setCarouselData(initialCards);
    initCounters(items.length);
    hasTrackedRef.current = false;
  }, [
    carouselDataSignature,
    initialCards,
    initialCardsSignature,
    items.length,
    initCounters,
  ]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!hasTrackedRef.current && trackingItems.length > 0) {
      const recommendationTitle =
        recommendationResponse?.groups[0]?.title ||
        t('searchHotelPage.hotelDeals.title');
      const currency = items[0]?.details?.currency?.toUpperCase() || '';

      const analyticsParams = SearchWidgetEvent.hotelDealsViewedEvent({
        recommendationTitle,
        currency,
        items: trackingItems,
      });

      logEvent(EVENT_NAMES.HOTEL_DEALS_VIEWED, analyticsParams);
      hasTrackedRef.current = true;
    }
  }, [
    trackingItems,
    logEvent,
    EVENT_NAMES.HOTEL_DEALS_VIEWED,
    recommendationResponse,
    t,
    items,
  ]);

  if ((!isLoading || error) && carouselData?.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <View style={styles.dealsContainer} testID="hotel-deals-loader">
        <SkeletonLoaderWrapper
          height={150}
          itemWidth={150}
          itemCount={3}
          orientation="horizontal"
          containerStyle={{ gap: pd.ref.space.spacing10.px }}
        />
      </View>
    );
  }

  if (!carouselData?.length) return null;

  return (
    <View style={styles.dealsContainer}>
      <Text
        style={[
          getTextStyle(
            pd.sys.typescale.body_text_medium,
            pd.sys.colour.content_primary,
          ),
          styles.label,
        ]}
      >
        {recommendationResponse?.groups[0]?.title ||
          t('searchHotelPage.hotelDeals.title')}
      </Text>

      <View style={styles.cardContainer}>
        <ContentCardGroupView
          orientation={Constants.CAROUSEL_ORIENTATION}
          contentCardProps={carouselData}
          viewHeight={Constants.CAROUSEL_HEIGHT}
          //@ts-expect-error renderItem already passed.
          flatListProps={{
            decelerationRate: Constants.SCROLL_SPEED,
            showsHorizontalScrollIndicator: false,
            onScroll: handleScroll,
          }}
          removeEdgeItemMargins={true}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    // Match ContentCardView's internal padding on native and web.
    marginLeft: -pd.ref.space.spacing10.px,
  },
  label: {
    marginBottom: 8,
  },
  dealsContainer: {
    paddingBottom: pd.ref.space.spacing30.px,
    paddingLeft: pd.ref.space.spacing40.px,
  },
});

export default HotelDealsCarouselView;
